import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Pencil, Trash2, Search, Filter, X, Upload,
  FileText, ExternalLink, Receipt, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Expense, EXPENSE_TYPES } from '../types';
import { formatCurrency } from '../utils/numberFormat';

const EMPTY_FORM = {
  expenseType: '',
  title: '',
  amount: '',
  expenseDate: format(new Date(), 'yyyy-MM-dd'),
  description: '',
};

const ExpensePage: React.FC = () => {
  const [expenses, setExpenses]     = useState<Expense[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState<number | null>(null);
  const [form, setForm]             = useState({ ...EMPTY_FORM });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Filters
  const [page, setPage]                     = useState(1);
  const [search, setSearch]                 = useState('');
  const [filterType, setFilterType]         = useState('');
  const [filterStart, setFilterStart]       = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [filterEnd, setFilterEnd]           = useState(format(new Date(), 'yyyy-MM-dd'));
  const [totalExpenses, setTotalExpenses]   = useState(0);

  const PAGE_SIZE = 20;

  useEffect(() => { loadExpenses(); }, [page, filterType, filterStart, filterEnd]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const res = await apiService.getExpenses({
        page, pageSize: PAGE_SIZE,
        expenseType: filterType || undefined,
        startDate: filterStart || undefined,
        endDate:   filterEnd   || undefined,
        search:    search      || undefined,
      });
      if (res.isSuccess) {
        setExpenses(res.entity.items ?? []);
        setTotalCount(res.entity.totalCount ?? 0);
        setTotalPages(res.entity.totalPages ?? 1);
        const sum = (res.entity.items ?? []).reduce((acc: number, e: Expense) => acc + e.amount, 0);
        setTotalExpenses(sum);
      } else {
        toast.error(res.message ?? 'Failed to load expenses');
      }
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadExpenses();
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setSelectedFile(null);
    setShowForm(true);
  };

  const openEdit = (expense: Expense) => {
    setEditingId(expense.id);
    setForm({
      expenseType: expense.expenseType,
      title:       expense.title,
      amount:      expense.amount.toString(),
      expenseDate: expense.expenseDate.slice(0, 10),
      description: expense.description ?? '',
    });
    setSelectedFile(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.expenseType) { toast.error('Expense type is required'); return; }
    if (!form.title)        { toast.error('Title is required');        return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Valid amount is required'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('expenseType', form.expenseType);
      fd.append('title',       form.title);
      fd.append('amount',      form.amount);
      fd.append('expenseDate', form.expenseDate);
      fd.append('description', form.description);
      fd.append('userId',      '1');
      if (selectedFile) fd.append('file', selectedFile);

      const res = editingId
        ? await apiService.updateExpense(editingId, fd)
        : await apiService.createExpense(fd);

      if (res.isSuccess) {
        toast.success(editingId ? 'Expense updated' : 'Expense created');
        closeForm();
        loadExpenses();
      } else {
        toast.error(res.message ?? 'Save failed');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      const res = await apiService.deleteExpense(id);
      if (res.isSuccess) {
        toast.success('Expense deleted');
        loadExpenses();
      } else {
        toast.error('Delete failed');
      }
    } catch {
      toast.error('Delete failed');
    }
  };

  const typeColor: Record<string, string> = {
    'Rent':              'badge badge-neutral',
    'Salary':            'badge badge-info',
    'Parts / Inventory': 'badge badge-success',
    'Utilities':         'badge badge-neutral',
    'Marketing':         'badge badge-info',
    'Transportation':    'badge badge-neutral',
    'Equipment':         'badge badge-success',
    'Miscellaneous':     'badge badge-neutral',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg flex items-center justify-center">
            <Receipt className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
            <p className="text-sm text-gray-500">Track and manage business expenses</p>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add Expense</span>
        </button>
      </div>

      {/* Summary bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-6">
        <div>
          <p className="text-xs text-gray-500">Total Expenses (this view)</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Records</p>
          <p className="text-xl font-bold text-gray-800">{totalCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Title / description..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="input-field pl-9 w-52"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">Type</label>
            <select
              value={filterType}
              onChange={e => { setFilterType(e.target.value); setPage(1); }}
              className="input-field w-44"
            >
              <option value="">All Types</option>
              {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={filterStart} onChange={e => { setFilterStart(e.target.value); setPage(1); }} className="input-field" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={filterEnd} onChange={e => { setFilterEnd(e.target.value); setPage(1); }} className="input-field" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">Quick Filter</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  setFilterStart(format(startOfMonth(now), 'yyyy-MM-dd'));
                  setFilterEnd(format(now, 'yyyy-MM-dd'));
                  setPage(1);
                }}
                className="px-3 py-2 text-sm font-medium rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                This Month
              </button>
              <button
                type="button"
                onClick={() => {
                  const lastMonth = subMonths(new Date(), 1);
                  setFilterStart(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
                  setFilterEnd(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
                  setPage(1);
                }}
                className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                Last Month
              </button>
            </div>
          </div>
          <button type="submit" className="btn-primary flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Apply</span>
          </button>
          <button
            type="button"
            onClick={() => { setSearch(''); setFilterType(''); setFilterStart(format(subDays(new Date(), 30), 'yyyy-MM-dd')); setFilterEnd(format(new Date(), 'yyyy-MM-dd')); setPage(1); loadExpenses(); }}
            className="btn-secondary"
          >
            Reset
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : expenses.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No expenses found</p>
            <p className="text-sm text-gray-400 mt-1">Add your first expense to get started</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">File</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {expenses.map(exp => (
                <tr key={exp.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {exp.expenseDate.slice(0, 10)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={typeColor[exp.expenseType] ?? 'badge badge-neutral'}>
                      {exp.expenseType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{exp.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{exp.description ?? '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-red-600 text-right whitespace-nowrap">
                    {formatCurrency(exp.amount)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {exp.fileUrl ? (
                      <a
                        href={exp.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-primary-600 hover:text-primary-700"
                        title={exp.fileName ?? 'View file'}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : (
                      <span className="text-gray-300"><FileText className="h-4 w-4 inline" /></span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEdit(exp)}
                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(exp.id)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {totalCount} records
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Expense' : 'Add Expense'}
              </h2>
              <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {/* Expense Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.expenseType}
                  onChange={e => setForm(f => ({ ...f, expenseType: e.target.value }))}
                  className="input-field w-full"
                  required
                >
                  <option value="">Select type...</option>
                  {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Monthly office rent"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="input-field w-full"
                  required
                />
              </div>

              {/* Amount + Date side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="input-field w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.expenseDate}
                    onChange={e => setForm(f => ({ ...f, expenseDate: e.target.value }))}
                    className="input-field w-full"
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={2}
                  placeholder="Optional notes..."
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="input-field w-full resize-none"
                />
              </div>

              {/* File upload (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt / Invoice <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  {selectedFile ? (
                    <p className="text-sm font-medium text-primary-600">{selectedFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">Click to upload</p>
                      <p className="text-xs text-gray-400">PDF, JPG, PNG up to 10 MB</p>
                    </>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
                {selectedFile && (
                  <button
                    type="button"
                    onClick={() => { setSelectedFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="mt-1 text-xs text-red-500 hover:underline"
                  >
                    Remove file
                  </button>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-end space-x-3 pt-2">
                <button type="button" onClick={closeForm} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensePage;
